'use client'

import type { PrebuildTenantsQuery } from '@/generated/graphql'
import { getDefaultTenant, getTenantFromDomain, getTenantFromWindow } from '@/helpers/tenants'

import { useRouter } from 'next/compat/router'
import React, { createContext, useContext, useMemo } from 'react'

type Tenant = PrebuildTenantsQuery['tenants']['rows'][number]

const TenantContext = createContext<Tenant | undefined>(undefined)

export const TenantProvider: React.FC<{
  tenant: Tenant | undefined
  children: React.ReactNode
}> = ({ tenant, children }) =>
  React.createElement(TenantContext.Provider, { value: tenant }, children)

export const useTenantStrict = () => {
  const contextTenant = useContext(TenantContext)

  const legacyRouter = useRouter()

  return useMemo(() => {
    if (contextTenant) return contextTenant

    const domain = legacyRouter?.query?.domain
    if (domain) return getTenantFromDomain(domain.toString())

    if (typeof window !== 'undefined') return getTenantFromWindow()

    return undefined
  }, [contextTenant, legacyRouter])
}

const useTenant = () => {
  const strictTenant = useTenantStrict()

  return useMemo(() => {
    if (strictTenant) return strictTenant
    const fallback = getDefaultTenant()
    if (!fallback) throw new Error('No default tenant found')
    return fallback
  }, [strictTenant])
}

export default useTenant
